import type { TotemIconName } from '@totem/icons';

// Marchés ------------------------------------------------------------------------------------------

const MarcheCodes = [
  'sante',
  'prevoyance',
  'emprunteur',
  'financement',
  'epargne-retraite',
  'iard',
] as const;

export type MarcheCode = (typeof MarcheCodes)[number];

export const isMarcheCode = (code: unknown): code is MarcheCode => {
  return typeof code === 'string' && MarcheCodes.includes(code as MarcheCode);
};

// Rubriques ------------------------------------------------------------------------------------------
// Une rubrique peut contenir plusieurs marchés

// A n'utiliser que pour le satisfies sinon OffreRubriqueCode juste après n'est pas déduit correctement
interface OffreRubriqueInternal {
  code: string;
  label: string;
  iconName: TotemIconName;
  circleIconName: TotemIconName;
}

// Contient les détails les plus couramment utilisés des rubriques des offres
export const OffreRubriqueMap = Object.freeze({
  SANTE_INDIVIDUELLE: {
    code: 'sante-individuelle',
    label: 'Santé individuelle',
    iconName: 'sante',
    circleIconName: 'sante-circle',
  },
  PREVOYANCE: {
    code: 'prevoyance',
    label: 'Prévoyance',
    iconName: 'prevoyance',
    circleIconName: 'prevoyance-circle',
  },
  EMPRUNTEUR_FINANCEMENT: {
    code: 'emprunteur-financement',
    label: 'Emprunteur et Financement',
    iconName: 'emprunteur',
    circleIconName: 'emprunteur-circle',
  },
  COLLECTIF: {
    code: 'collectif',
    label: 'Collectif',
    iconName: 'collectif',
    circleIconName: 'collectif-circle',
  },
  EPARGNE_RETRAITE: {
    code: 'epargne-retraite',
    label: 'Epargne retraite',
    iconName: 'epargne-retraite',
    circleIconName: 'epargne-retraite-circle',
  },
  IARD: {
    code: 'iard',
    label: 'IARD',
    iconName: 'iard',
    circleIconName: 'iard-circle',
  },
} as const satisfies Record<string, OffreRubriqueInternal>);

export type OffreRubriqueCode = (typeof OffreRubriqueMap)[keyof typeof OffreRubriqueMap]['code'];

export interface OffreRubrique extends OffreRubriqueInternal {
  code: OffreRubriqueCode;
}

export const offresRubriques: readonly OffreRubrique[] = Object.freeze(Object.values(OffreRubriqueMap));

export function isOffreRubriqueCode(code: unknown): code is OffreRubriqueCode {
  return typeof code === 'string' && offresRubriques.some(rubrique => rubrique.code === code);
}

export const getOffreRubrique = (offreRubriqueCode?: OffreRubriqueCode): OffreRubrique | undefined => {
  return offresRubriques.find(({ code }) => code === offreRubriqueCode);
};

// Marque client ------------------------------------------------------------------------------------------

export type MarqueClientCode =
  | 'alptis'
  | 'miltis'
  | 'msh'
  | 'unep'
  | 'capvita'
  | 'belend'
  | 'pret-pro'
  | 'groupe-mncap'
  | 'oradea-vie'
  | 'cnp-assurances'
  | 'malakoff-humanis'
  | 'insured'
  | 'alptisxunep';

export type PartenairesMarqueClientCode =
  | 'unep'
  | 'pret-pro'
  | 'insured'
  | 'signature-assurances';

// Réseau ------------------------------------------------------------------------------------------

export const ReseauDistributionCodeMap = {
  ALPTIS: 'ALPTIS',
  MILTIS: 'MILTIS',
} as const;

export type ReseauDistributionCode = (typeof ReseauDistributionCodeMap)[keyof typeof ReseauDistributionCodeMap];
